import React from "react";
import { useTheme } from "react-jss";
import { Scrollbars } from "react-custom-scrollbars";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./ScrollableContainer.styles";

interface Props {
    children?: any;
}

enum ScrollDirection {
    Left = -1,
    Right = 1,
}

function ScrollableIndicator({ onClick, direction, showIndicator }) {
    const theme = useTheme();
    const classes = useStyles({ theme });

    const [icon, indicatorClass] =
        direction === ScrollDirection.Left
            ? ["chevron-left", classes.indicatorLeft]
            : ["chevron-right", classes.indicatorRight];

    return (
        <div
            className={classnames(
                classes.indicator,
                indicatorClass,
                !showIndicator && classes.indicatorHidden,
            )}
            onClick={onClick}>
            <FontAwesomeIcon icon={icon as IconProp} className={classes.icon} />
        </div>
    );
}

const defaultScrollValues = { scrollLeft: 0, scrollWidth: 0, clientWidth: 0 };
const deadzone = 29;

export default function ScrollableContainer(props: Props) {
    const theme = useTheme();
    const scrollbarRef = React.useRef<Scrollbars>(null);
    const classes = useStyles({ theme });
    const toggleIndicators = React.useCallback(({ scrollLeft, scrollWidth, clientWidth }) => {
        const isContainerNarrow = clientWidth < scrollWidth;
        setShowLeftScrollIndicator(isContainerNarrow && scrollLeft > deadzone);
        setShowRightScrollIndicator(
            isContainerNarrow && scrollLeft + clientWidth < scrollWidth - deadzone,
        );
    }, []);

    React.useEffect(() => {
        window.addEventListener("load", toggleIndicators);
        return () => {
            window.removeEventListener("load", toggleIndicators);
        };
    }, []);

    const [showLeftScrollIndicator, setShowLeftScrollIndicator] = React.useState(false);
    const [showRightScrollIndicator, setShowRightScrollIndicator] = React.useState(false);
    const [{ scrollLeft, scrollWidth, clientWidth }, setScrollValues] = React.useState(
        defaultScrollValues,
    );
    const { children } = props;

    const scrollBy = React.useCallback((value: number) => {
        if (scrollbarRef.current.view)
            scrollbarRef.current.view.scrollBy({
                left: value,
                behavior: "smooth",
            });
    }, []);

    React.useEffect(() => {
        const isContainerNarrow = clientWidth < scrollWidth;
        const hasMoreLeft = scrollLeft > deadzone;
        const shouldShowLeftScrollIndicator = isContainerNarrow && hasMoreLeft;

        setShowLeftScrollIndicator(shouldShowLeftScrollIndicator);

        const hasMoreRight = scrollLeft + clientWidth < scrollWidth - deadzone;
        const shouldShowRightScrollIndicator = isContainerNarrow && hasMoreRight;

        setShowRightScrollIndicator(shouldShowRightScrollIndicator);
    }, [clientWidth, scrollWidth, scrollLeft]);

    function scrollPage(direction: ScrollDirection) {
        scrollBy(clientWidth * direction);
    }

    return (
        <div className={classes.scrollable}>
            <ScrollableIndicator
                onClick={() => scrollPage(ScrollDirection.Left)}
                direction={ScrollDirection.Left}
                showIndicator={showLeftScrollIndicator}
            />
            <Scrollbars
                className={classnames(classes.scrollbars)}
                ref={scrollbarRef}
                autoHide={false}
                autoHeight
                autoHeightMax={Number.MAX_SAFE_INTEGER}
                onUpdate={(values) => {
                    toggleIndicators(values);
                    setScrollValues(values);
                }}
                renderTrackHorizontal={(trackProps) => (
                    <div {...trackProps} className={classnames(classes.scrollTrackHorizontal)} />
                )}
                renderThumbHorizontal={(thumbProps) => (
                    <div {...thumbProps} className={classes.scrollThumbHorizontal} />
                )}
                renderView={(viewProps) => {
                    return <div {...viewProps} className={classes.scrollableWindow} />;
                }}
            >
                <div className={classes.scrollableChild}>{children}</div>
            </Scrollbars>
            <ScrollableIndicator
                onClick={() => scrollPage(ScrollDirection.Right)}
                direction={ScrollDirection.Right}
                showIndicator={showRightScrollIndicator}
            />
        </div>
    );
}
